import { useState, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import aiSummarizationService from '../../../services/aiSummarizationService';
import { protectedResources } from '../../../config/msalConfig';

export const useStreamingResponse = () => {
  const { instance, accounts } = useMsal();
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState('');
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  const getAuthToken = async () => {
    const request = {
      scopes: protectedResources.api.scopes.read,
      account: accounts[0],
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      return null;
    }
  };

  const streamResponse = useCallback(async (query, options = {}) => {
    setIsStreaming(true);
    setStreamedContent('');
    setError(null);

    try {
      // Get access token
      const accessToken = await getAuthToken();
      if (!accessToken) {
        throw new Error('Failed to get access token');
      }

      // For now, use non-streaming API since backend has streaming disabled
      const response = await aiSummarizationService.processAICopilotQuery({
        query,
        userId: options.userId,
        organizationId: options.organizationId,
        conversationId: options.conversationId,
      }, accessToken);

      // Simulate streaming by displaying the complete response
      setStreamedContent(response.data.response);
      setIsStreaming(false);

      // Call onComplete callback
      if (options.onComplete) {
        options.onComplete({
          content: response.data.response,
          sources: response.data.sources || [],
          metadata: response.data.metadata || {},
        });
      }

    } catch (err) {
      console.error('Streaming error:', err);
      setError(err.message || 'Failed to connect to AI service');
      setIsStreaming(false);
    }
  }, [instance, accounts]);

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    streamResponse,
    cancelStream,
    isStreaming,
    streamedContent,
    error,
  };
};
